/**
 * Fetches the latest saved medical PA form data for an order/form on mount.
 * @module useLatestMedicalPaFormData
 */
import { useEffect, useState } from "react";
import {
  getLatestMedicalPaFormData,
  LatestMedicalPaFormData,
} from "../api/getCalls/getLatestMedicalPaFormData";
import { logError } from "../utils/customLogger";

/** Inputs for {@link useLatestMedicalPaFormData}. */
export interface UseLatestMedicalPaFormDataParams {
  /** PA order ID whose latest form data should be fetched. */
  orderId: string;
  /** Organisation ID scoping the query. */
  orgId: string;
  /** Form/template ID selected for the order. */
  formId: string;
}

/** State returned by {@link useLatestMedicalPaFormData}. */
export interface UseLatestMedicalPaFormDataReturn {
  /** Latest form-data payload, or `null` before it resolves. */
  data: LatestMedicalPaFormData | null;
  /** `true` while the request is in flight. */
  loading: boolean;
  /** Error message when the fetch fails, otherwise `null`. */
  error: string | null;
}

/**
 * Loads the latest medical PA form data via {@link getLatestMedicalPaFormData}
 * whenever `orderId`, `orgId`, and `formId` are all present. The in-flight
 * request is aborted on unmount or when any input changes.
 *
 * @param params - Order ID, org ID, and form ID used as query parameters.
 * @returns The fetched data along with loading and error state.
 */
const useLatestMedicalPaFormData = ({
  orderId,
  orgId,
  formId,
}: UseLatestMedicalPaFormDataParams): UseLatestMedicalPaFormDataReturn => {
  const [data, setData] = useState<LatestMedicalPaFormData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId || !orgId || !formId) {
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    getLatestMedicalPaFormData(
      { order_id: orderId, org_id: orgId, form_id: formId },
      controller.signal,
    )
      .then((response) => {
        setData(response);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name === "AbortError") {
          return;
        }
        logError(err, "Failed to fetch latest medical PA form data");
        setError(err.message);
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [orderId, orgId, formId]);

  return { data, loading, error };
};

export default useLatestMedicalPaFormData;
